// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { ConnectButton, useCurrentWallet, useCurrentAccount, useSignTransaction, useSuiClient } from '@mysten/dapp-kit';
import { Transaction } from '@mysten/sui/transactions';
import { Button, Text, VStack, HStack, Box, Progress, useToast, Link, Input, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, Stack, Spinner } from "@chakra-ui/react";
import { KioskClient, Network, KioskTransaction } from '@mysten/kiosk';
import { motion, AnimatePresence } from 'framer-motion';

// Constants for Surge NFT
const PACKAGE_ID = '0xbbeefd8d80c5fb9c650f6b22666ae907c5d0ae4263b09f7756eb19b082451b39';
const COLLECTION_OBJECT_ID = '0x2b0b644fa51642f7b1b447e28489e4b567ab0271efbea56f8ba2d35f8bb502cc';
const TRANSFER_POLICY_ID = '0xe21edc6cbdbe0564ae6c9cf2103afa388e19daf63b12f6e7765f22bc872e26d6';
const RANDOM_ID = '0x8';

// Collection constants
const MAX_SUPPLY = 1111;
const TEAM_RESERVE = 11;
const PHASE1_ALLOC = 0;
const PHASE2_ALLOC = 500;

// Phase pricing
const PHASE1_PRICE = 50_000_000_000;
const PHASE1_PRICE_AFTER = 50_000_000_000;
const PHASE2_PRICE = 50_000_000_000;
const PUBLIC_PRICE = 50_000_000_000;

// Phase limits
const PHASE1_MAX = 3;
const PHASE2_MAX = 5;
const PUBLIC_MAX = 10;

// Walrus hash IDs for NFT display
const WALRUS_HASH_IDS = [
  "V1eqe19Q9BFezjAw1BT9_vz5G1o3eVCGv180Sa4JVNQ",
  "wP2IGWrhaigSSit_4JD1CZxKUlVpYrq0gwOqoveXQvE",
  "3CG3eYPhElaB2czvWOkqSh1t3-Ufc5SKnOXGxzePXoI",
  "Tqc2xKzqWjKQop81DNo6ZU2zlY6pMNayC5ONZQvnhC0",
  "I2EtF-rcL7P4E5GrBpy3tFT0qGreNMNy7lyVYebA9Co",
  "BU1mdJpE-UJaTOSglM-PT6Z6rZFKKb5viBcMUy2jQE8",
  "QpBc62lD3c6-d6A1birw95czm5HRr7UwQOtbuZSUmbY"
];

const DEBUG_MODE = false;
const PHASE_DURATION = 1800000;
const MINT_START_TIME = 1745607600000;

// Style constants
const BUTTON_STYLES = {
    bg: "rgba(0, 0, 0, 0.3)",
    color: "white",
    backdropFilter: "blur(10px)",
    border: "1px solid",
    borderColor: "whiteAlpha.200",
    _hover: {
        bg: "rgba(0, 0, 0, 0.4)",
        transform: "translateY(-2px)",
        boxShadow: "0 0 20px rgba(187, 134, 252, 0.1)"
    },
    transition: "all 0.3s ease",
    height: "45px",
    fontSize: "sm"
};

const QUANTITY_INPUT_STYLES = {
    bg: "rgba(0, 0, 0, 0.3)",
    color: "white",
    backdropFilter: "blur(10px)",
    border: "1px solid",
    borderColor: "whiteAlpha.200",
    _hover: {
        borderColor: "whiteAlpha.400"
    },
    height: "45px"
};

// NFT Display Component
const NFTDisplay = () => {
  const [isMuted, setIsMuted] = useState(true);
  
  return (
    <Box 
      position="relative" 
      width="100%"
      height={{ base: "300px", md: "500px" }}
      overflow="hidden"
    >
      <video
        src="/surge.mp4"
        autoPlay
        loop
        muted={isMuted}
        playsInline
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          position: 'absolute',
        }}
      />
      <Button
        position="absolute"
        bottom="10px"
        right="10px"
        size="sm"
        bg="rgba(0, 0, 0, 0.5)"
        color="white"
        _hover={{ bg: 'rgba(0, 0, 0, 0.7)' }}
        onClick={() => setIsMuted(!isMuted)}
      >
        {isMuted ? '🔇' : '🔊'}
      </Button>
    </Box>
  );
};

// Stats Card Component
const StatsCard = ({ stats, constants, mintingPhase, timeRemaining }) => {
    return (
        <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
            style={{ width: '100%' }}
        >
            <Box
                bg="rgba(0, 0, 0, 0.3)"
                backdropFilter="blur(10px)"
                borderRadius="xl"
                p={4}
                width="100%"
                minWidth={{ base: "100%", md: "400px" }}
                border="1px solid"
                borderColor="whiteAlpha.200"
                _hover={{
                    bg: "rgba(0, 0, 0, 0.4)",
                    boxShadow: "0 0 20px rgba(187, 134, 252, 0.1)"
                }}
                transition="all 0.3s ease"
            >
                <VStack spacing={4}>
                    {/* Phase Information */}
                    <Box width="100%" pb={4} borderBottom="1px solid" borderColor="whiteAlpha.200">
                        {mintingPhase === 0 && (
                            <VStack spacing={2} width="100%">
                                <HStack width="100%" justify="space-between">
                                    <Text fontSize="sm" color="white">Minting Starts In:</Text>
                                    <Text fontSize="sm" color="cyan.300">{timeRemaining}</Text>
                                </HStack>
                                <Progress 
                                    value={(timeRemaining / (stats.mint_start - Date.now())) * 100} 
                                    isIndeterminate={false}
                                    width="100%"
                                    size="sm"
                                    colorScheme="purple"
                                />
                            </VStack>
                        )}
                        {mintingPhase === 1 && (
                            <VStack spacing={2} width="100%">
                                <HStack width="100%" justify="space-between">
                                    <Text fontSize="sm" color="white">Phase Ends In:</Text>
                                    <Text fontSize="sm" color="cyan.300">{timeRemaining}</Text>
                                </HStack>
                                <Progress 
                                    value={(timeRemaining / PHASE_DURATION) * 100}
                                    isIndeterminate={false}
                                    width="100%"
                                    size="sm"
                                    colorScheme="purple"
                                />
                            </VStack>
                        )}
                        {mintingPhase === 2 && (
                            <HStack width="100%" justify="space-between">
                                <Text fontSize="sm" color="white">Public Mint Active</Text>
                                <Text fontSize="sm" color="green.300">Live</Text>
                            </HStack>
                        )}
                    </Box>

                    {/* Collection Stats */}
                    <VStack spacing={2} width="100%">
                        <HStack width="100%" justify="space-between">
                            <Text fontSize="sm" color="white">Total Minted:</Text>
                            <Text fontSize="sm" color="white">{stats.minted} / {constants.MAX_SUPPLY}</Text>
                        </HStack>
                        <HStack width="100%" justify="space-between">
                            <Text fontSize="sm" color="cyan.300">WL Minted:</Text>
                            <Text fontSize="sm" color="cyan.300">{stats.phase2_minted} / {constants.PHASE2_ALLOC}</Text>
                        </HStack>
                        <HStack width="100%" justify="space-between">
                            <Text fontSize="sm" color="green.300">Public Minted:</Text>
                            <Text fontSize="sm" color="green.300">{stats.public_minted}</Text>
                        </HStack>
                        <HStack width="100%" justify="space-between">
                            <Text fontSize="sm" color="orange.300">Remaining:</Text>
                            <Text fontSize="sm" color="orange.300">{constants.MAX_SUPPLY - stats.minted}</Text>
                        </HStack>
                    </VStack>

                    {/* Pricing Information */}
                    <Box width="100%" pt={4} borderTop="1px solid" borderColor="whiteAlpha.200">
                        <VStack spacing={2} align="start">
                            <Text fontSize="sm" color="white" fontWeight="bold">Mint Prices:</Text>
                            <Text fontSize="sm" color="cyan.300">• WL: 50 SUI</Text>
                            <Text fontSize="sm" color="green.300">• Public: 50 SUI</Text>
                        </VStack>
                    </Box>
                </VStack>
            </Box>
        </motion.div>
    );
};

const FullScreenOverlay = ({ onClose }) => {
    const { currentWallet, connectionStatus } = useCurrentWallet();
    const { mutateAsync: signTransaction } = useSignTransaction();
    const client = useSuiClient();
    const toast = useToast();
    const account = useCurrentAccount();
    
    // Collection state
    const [collectionState, setCollectionState] = useState(DEBUG_MODE ? {
        minted: TEAM_RESERVE,
        phase1_minted: 0,
        phase2_minted: 0,
        public_minted: 0,
        mint_start: MINT_START_TIME,
        phase_end: MINT_START_TIME + PHASE_DURATION,
        is_public_mint_active: false,
    } : {
        minted: TEAM_RESERVE,
        phase1_minted: 0,
        phase2_minted: 0,
        public_minted: 0,
        mint_start: MINT_START_TIME,
        phase_end: MINT_START_TIME + PHASE_DURATION,
        is_public_mint_active: false,
    });

    // UI state
    const [mintingPhase, setMintingPhase] = useState(0);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [isMinting, setIsMinting] = useState(false);
    const [collabQuantity, setCollabQuantity] = useState(1);
    const [publicQuantity, setPublicQuantity] = useState(1);
    const [isLoadingEligibility, setIsLoadingEligibility] = useState(false);

    // Phase state
    const [phase2Eligible, setPhase2Eligible] = useState(false);
    const [phase2Remaining, setPhase2Remaining] = useState(0);
    const [phasesActive, setPhasesActive] = useState(false);
    const [publicMintActive, setPublicMintActive] = useState(false);
    const [walletMints, setWalletMints] = useState({ phase2: 0, public: 0 });

    // Kiosk state
    const [kioskInfo, setKioskInfo] = useState(null);

    const kioskClient = new KioskClient({
        client,
        network: Network.MAINNET,
    });

    // Helper functions
    const formatTime = (milliseconds) => {
        if (milliseconds < 0) return "0d 0h 0m 0s";
        const seconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        return `${days}d ${hours % 24}h ${minutes % 60}m ${seconds % 60}s`;
    };

    const handleCollabQuantityChange = (valueString) => {
        const value = parseInt(valueString);
        if (isNaN(value) || value < 1) return;
        setCollabQuantity(value);
    };

    const handlePublicQuantityChange = (valueString) => {
        const value = parseInt(valueString);
        if (isNaN(value) || value < 1) return;
        setPublicQuantity(value);
    };

    // Kiosk management functions
    const storeKioskInfo = (address, kioskId, kioskCapId) => {
        const kioskData = { kioskId, kioskCapId, address };
        localStorage.setItem('kioskInfo', JSON.stringify(kioskData));
        setKioskInfo(kioskData);
    };

    const getStoredKioskInfo = async (address) => {
        const storedData = localStorage.getItem('kioskInfo');
        if (storedData) {
            const parsedData = JSON.parse(storedData);
            if (parsedData.address === address) {
                return parsedData;
            }
        }
        return null;
    };

    const verifyKiosk = async (kioskId, kioskCapId) => {
        try {
            const kioskObject = await client.getObject({
                id: kioskId,
                options: { showContent: true }
            });
            const capObject = await client.getObject({
                id: kioskCapId,
                options: { showContent: true }
            });
            return kioskObject.data && capObject.data;
        } catch (error) {
            console.error('Error verifying kiosk:', error);
            return false;
        }
    };

    // State management functions
    const fetchCollectionState = async () => {
        if (!DEBUG_MODE) {
            try {
                const collectionObject = await client.getObject({
                    id: COLLECTION_OBJECT_ID,
                    options: { showContent: true }
                });

                if (collectionObject.data) {
                    const content = collectionObject.data.content.fields;
                    setCollectionState({
                        minted: 11,
                        phase1_minted: 0,
                        phase2_minted: 0,
                        public_minted: 0,
                        mint_start: 1749607600000,
                        phase_end: 1749607600000+1800000,
                        is_public_mint_active: content.is_public_mint_active,
                    });
                }
            } catch (error) {
                console.error('Error fetching collection state:', error);
                toast({
                    title: "Error",
                    description: "Failed to fetch collection state",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        }
    };

    const fetchUserEligibility = async () => {
        if (!DEBUG_MODE && account) {
            setIsLoadingEligibility(true);
            try {
                const txb = new Transaction();
                txb.moveCall({
                    target: `${PACKAGE_ID}::Surge::get_wallet_allocations`,
                    arguments: [
                        txb.object(COLLECTION_OBJECT_ID),
                        txb.pure.address(account.address)
                    ]
                });

                const result = await client.devInspectTransactionBlock({
                    sender: account.address,
                    transactionBlock: txb
                });

                let phase2_qualified = false;
                let phase2_remaining = 0;

                if (result.results && result.results[0] && result.results[0].returnValues) {
                    const returnValues = result.results[0].returnValues;
                    phase2_qualified = returnValues[2][0][0] === 1;
                    phase2_remaining = returnValues[3][0][0];
                }

                setPhase2Eligible(phase2_qualified);
                setPhase2Remaining(phase2_remaining);

                if (phase2_qualified) {
                    setWalletMints(prev => ({
                        ...prev,
                        phase2: PHASE2_MAX - phase2_remaining
                    }));
                }
            } catch (error) {
                console.error('Error checking eligibility:', error);
            } finally {
                setIsLoadingEligibility(false);
            }
        }
    };

    // Minting functions
    const mintPhase2 = async () => {
        if (!account) return;
        
        try {
            setIsMinting(true);
            
            let kioskId, kioskCapId;
            
            if (!kioskInfo) {
                const tx = new Transaction();
                const kioskTx = new KioskTransaction({ transaction: tx, kioskClient });
                
                kioskTx.create();
                kioskTx.shareAndTransferCap(account.address);
                kioskTx.finalize();
                
                const { bytes, signature } = await signTransaction({
                    transaction: tx,
                    chain: 'sui:mainnet',
                });
                
                const result = await client.executeTransactionBlock({
                    transactionBlock: bytes,
                    signature,
                    options: {
                        showEffects: true,
                        showInput: true,
                        showRawEffects: true,
                    },
                });
                
                const txBlock = await client.getTransactionBlock({
                    digest: result.digest,
                    options: { showEffects: true, showInput: true },
                });
                
                if (txBlock.effects && txBlock.effects.created) {
                    txBlock.effects.created.forEach(obj => {
                        if (obj.owner.Shared) {
                            kioskId = obj.reference.objectId;
                        } else if (obj.owner.AddressOwner === account.address) {
                            kioskCapId = obj.reference.objectId;
                        }
                    });
                }
                
                if (!kioskId || !kioskCapId) {
                    throw new Error('Failed to retrieve kiosk or kiosk cap ID');
                }
                
                storeKioskInfo(account.address, kioskId, kioskCapId);
            } else {
                kioskId = kioskInfo.kioskId;
                kioskCapId = kioskInfo.kioskCapId;
            }

            const cappedQuantity = Math.min(collabQuantity, PHASE2_MAX - walletMints.phase2);
            const totalPayment = PHASE2_PRICE * cappedQuantity;
            
            const tx = new Transaction();
            const [paymentCoin] = tx.splitCoins(tx.gas, [tx.pure.u64(totalPayment)]);
            
            tx.moveCall({
                target: `${PACKAGE_ID}::Surge::mint_phase2`,
                arguments: [
                    tx.object(COLLECTION_OBJECT_ID),
                    paymentCoin,
                    tx.object(TRANSFER_POLICY_ID),
                    tx.object('0x6'),
                    tx.object(RANDOM_ID),
                    tx.object(kioskId),
                    tx.object(kioskCapId),
                    tx.pure.u64(cappedQuantity),
                ],
            });

            const { bytes, signature } = await signTransaction({
                transaction: tx,
                chain: 'sui:mainnet',
            });

            const result = await client.executeTransactionBlock({
                transactionBlock: bytes,
                signature,
                options: { showEffects: true, showInput: true },
            });

            await new Promise(resolve => setTimeout(resolve, 5000));
            await Promise.all([fetchCollectionState(), fetchUserEligibility()]);
            
            toast({
                title: "WL Phase Minting Successful",
                description: `Minted ${cappedQuantity} Surge NFTs`,
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error minting phase 2:', error);
            toast({
                title: "WL Phase Minting Error",
                description: error.message || "An error occurred during minting",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsMinting(false);
        }
    };

    const mintPublic = async () => {
        if (!account) return;
        
        try {
            setIsMinting(true);
            
            let kioskId, kioskCapId;
            
            if (!kioskInfo) {
                // Create kiosk logic (same as in mintPhase2)
                const tx = new Transaction();
                const kioskTx = new KioskTransaction({ transaction: tx, kioskClient });
                
                kioskTx.create();
                kioskTx.shareAndTransferCap(account.address);
                kioskTx.finalize();
                
                const { bytes, signature } = await signTransaction({
                    transaction: tx,
                    chain: 'sui:mainnet',
                });
                
                const result = await client.executeTransactionBlock({
                    transactionBlock: bytes,
                    signature,
                    options: {
                        showEffects: true,
                        showInput: true,
                        showRawEffects: true,
                    },
                });
                
                const txBlock = await client.getTransactionBlock({
                    digest: result.digest,
                    options: { showEffects: true, showInput: true },
                });
                
                if (txBlock.effects && txBlock.effects.created) {
                    txBlock.effects.created.forEach(obj => {
                        if (obj.owner.Shared) {
                            kioskId = obj.reference.objectId;
                        } else if (obj.owner.AddressOwner === account.address) {
                            kioskCapId = obj.reference.objectId;
                        }
                    });
                }
                
                if (!kioskId || !kioskCapId) {
                    throw new Error('Failed to retrieve kiosk or kiosk cap ID');
                }
                
                storeKioskInfo(account.address, kioskId, kioskCapId);
            } else {
                kioskId = kioskInfo.kioskId;
                kioskCapId = kioskInfo.kioskCapId;
            }

            const currentTime = Date.now();
            if (currentTime <= collectionState.phase_end) {
                throw new Error("Public minting is not active yet");
            }

            const totalPayment = PUBLIC_PRICE * publicQuantity;
            
            const tx = new Transaction();
            const [paymentCoin] = tx.splitCoins(tx.gas, [tx.pure.u64(totalPayment)]);
            
            tx.moveCall({
                target: `${PACKAGE_ID}::Surge::mint_public`,
                arguments: [
                    tx.object(COLLECTION_OBJECT_ID),
                    paymentCoin,
                    tx.object(TRANSFER_POLICY_ID),
                    tx.object('0x6'),
                    tx.object(RANDOM_ID),
                    tx.object(kioskId),
                    tx.object(kioskCapId),
                    tx.pure.u64(publicQuantity),
                ],
            });

            const { bytes, signature } = await signTransaction({
                transaction: tx,
                chain: 'sui:mainnet',
            });

            const result = await client.executeTransactionBlock({
                transactionBlock: bytes,
                signature,
                options: { showEffects: true, showInput: true },
            });

            await fetchCollectionState();
            
            toast({
                title: "Public Minting Successful",
                description: `Minted ${publicQuantity} Surge NFTs`,
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error in public mint:', error);
            toast({
                title: "Public Minting Error",
                description: error.message || "An error occurred during minting",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsMinting(false);
        }
    };

    // Effects
    useEffect(() => {
        if (!DEBUG_MODE && account) {
            fetchCollectionState();
            fetchUserEligibility();
        }
    }, [DEBUG_MODE, account]);

    useEffect(() => {
        if (!DEBUG_MODE) {
            fetchCollectionState();
            const interval = setInterval(fetchCollectionState, 10000);
            return () => clearInterval(interval);
        }
    }, [DEBUG_MODE]);

    useEffect(() => {
        const timerInterval = setInterval(() => {
            const currentTime = Date.now();
            
            if (currentTime < collectionState.mint_start) {
                setTimeRemaining(collectionState.mint_start - currentTime);
                setMintingPhase(0);
                setPhasesActive(false);
                setPublicMintActive(false);
            } else if (currentTime <= collectionState.phase_end) {
                setTimeRemaining(collectionState.phase_end - currentTime);
                setMintingPhase(1);
                setPhasesActive(true);
                setPublicMintActive(false);
            } else {
                setTimeRemaining(0);
                setMintingPhase(2);
                setPhasesActive(false);
                setPublicMintActive(true);
            }
        }, 1000);

        return () => clearInterval(timerInterval);
    }, [collectionState.mint_start, collectionState.phase_end]);

    useEffect(() => {
        const initializeKioskInfo = async () => {
            if (account) {
                const stored = await getStoredKioskInfo(account.address);
                if (stored) {
                    const isValid = await verifyKiosk(stored.kioskId, stored.kioskCapId);
                    if (isValid) {
                        setKioskInfo(stored);
                    } else {
                        localStorage.removeItem('kioskInfo');
                    }
                }
            }
        };

        initializeKioskInfo();
    }, [account, client]);

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 1000,
                overflow: 'auto',
                backgroundColor: 'black'
            }}
        >
            <Box 
                position="relative" 
                minH="100vh" 
                w="100%" 
                bg="black"
                sx={{
                    animation: 'fadeIn 0.3s ease-in-out',
                    '@keyframes fadeIn': {
                        '0%': {
                            backgroundColor: 'black',
                        },
                        '100%': {
                            backgroundColor: 'black',
                        }
                    },
                    '&::before': {
                        content: '""',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: "url('/bg.png')",
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundAttachment: 'fixed',
                        opacity: 0.2,
                        zIndex: 1,
                        animation: 'fadeInBg 0.5s ease-in-out',
                    },
                    '@keyframes fadeInBg': {
                        '0%': {
                            opacity: 0,
                        },
                        '100%': {
                            opacity: 0.2,
                        }
                    },
                    '&::after': {
                        content: '""',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        zIndex: 1,
                        animation: 'fadeInOverlay 0.5s ease-in-out',
                    },
                    '@keyframes fadeInOverlay': {
                        '0%': {
                            backgroundColor: 'rgba(0, 0, 0, 1)',
                        },
                        '100%': {
                            backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        }
                    }
                }}
            >
                {/* Header */}
                <header style={{ position: 'relative', zIndex: 20 }}>
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                        px={15}
                        py={4}
                        bg="transparent"
                        position="relative"
                        zIndex={20}
                    >
                        <img
                            src="/surge.png"
                            alt="Surge"
                            style={{ 
                                position: 'absolute', 
                                top: 25, 
                                left: 20, 
                                width: '100px',
                                objectFit: 'contain'
                            }}
                        />
                        
                        <HStack spacing={4} mr={175}>
                            <Link
                                href="https://x.com/0xairtx"
                                isExternal
                                _hover={{ transform: 'scale(1.1)' }}
                                transition="transform 0.2s"
                            >
                                <img
                                    src="/x.png"
                                    alt="X"
                                    width="18px"
                                    height="18px"
                                />
                            </Link>
                            <Button
                                onClick={onClose}
                                variant="ghost"
                                color="white"
                                _hover={{ bg: 'whiteAlpha.200' }}
                            >
                                Exit
                            </Button>
                        </HStack>

                        <Box style={{ 
                            position: 'absolute', 
                            top: 15, 
                            right: 20,
                            zIndex: 1000
                        }}>
                            <ConnectButton />
                        </Box>
                    </Box>
                </header>

                {/* Main Content */}
                <VStack spacing={5} align="center" justify="center" p={8} position="relative" zIndex={2}>
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={8}
                        width="100%"
                        maxWidth="500px"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <NFTDisplay />

                        {/* Minting Content */}
                        <VStack 
                            spacing={6} 
                            align="center"
                            justify="center"
                            width="100%"
                        >
                            {/* Minting Interface - Only visible when connected */}
                            {connectionStatus === 'connected' && (
                                <VStack spacing={4} align="center" width="100%">
                                    {isLoadingEligibility ? (
                                        <VStack spacing={4} width="100%" py={8}>
                                            <Spinner 
                                                size="xl" 
                                                color="purple.500" 
                                                thickness="4px"
                                                speed="0.65s"
                                            />
                                            <Text color="gray.500" fontSize="sm">
                                                Checking mint eligibility...
                                            </Text>
                                        </VStack>
                                    ) : (
                                        <>
                                            {/* Eligibility Status Text */}
                                            {!DEBUG_MODE && Date.now() < collectionState.mint_start && (
                                                <Text
                                                    fontSize="md"
                                                    fontWeight="bold"
                                                    textAlign="center"
                                                    color="black"
                                                    bg="rgba(255, 255, 255, 0.7)"
                                                    backdropFilter="blur(10px)"
                                                    p={4}
                                                    borderRadius="xl"
                                                    width="100%"
                                                >
                                                    {phase2Eligible
                                                        ? "Congratulations! You are eligible for WL Phase minting."
                                                        : "Public mint will be available 30 minutes after launch. Stay tuned!"}
                                                </Text>
                                            )}

                                            {/* Phase-specific mint buttons */}
                                            {phasesActive && (
                                                <VStack spacing={4} width="100%">
                                                    {phase2Eligible && walletMints.phase2 < PHASE2_MAX && (
                                                        <VStack width="100%" spacing={2} align="flex-start">
                                                            <Text fontSize="sm" color="white" fontWeight="bold">WL MINT</Text>
                                                            <HStack width="100%" spacing={4}>
                                                                <NumberInput
                                                                    min={1}
                                                                    max={PHASE2_MAX - walletMints.phase2}
                                                                    value={collabQuantity}
                                                                    onChange={handleCollabQuantityChange}
                                                                    width="100px"
                                                                    sx={{
                                                                        '& input': {
                                                                            bg: 'white',
                                                                            color: 'black',
                                                                        },
                                                                        '& button': {
                                                                            bg: 'white',
                                                                            color: 'black',
                                                                            borderColor: 'gray.200',
                                                                            _hover: {
                                                                                bg: 'gray.100'
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    <NumberInputField />
                                                                    <NumberInputStepper>
                                                                        <NumberIncrementStepper />
                                                                        <NumberDecrementStepper />
                                                                    </NumberInputStepper>
                                                                </NumberInput>
                                                                <Button
                                                                    onClick={mintPhase2}
                                                                    isLoading={isMinting}
                                                                    width="100%"
                                                                    bg="white"
                                                                    color="black"
                                                                    _hover={{ bg: 'gray.100' }}
                                                                    colorScheme="gray"
                                                                >
                                                                    <HStack justify="space-between" width="100%">
                                                                        <Text>Mint {collabQuantity} Surge</Text>
                                                                        <HStack>
                                                                            <Text>{(PHASE2_PRICE * Math.min(PHASE2_MAX - walletMints.phase2, collabQuantity)) / 1_000_000_000}</Text>
                                                                            <img src="/sui.svg" alt="SUI" width="15px" height="15px" />
                                                                        </HStack>
                                                                    </HStack>
                                                                </Button>
                                                            </HStack>
                                                        </VStack>
                                                    )}
                                                </VStack>
                                            )}

                                            {/* Public Mint Button */}
                                            {publicMintActive && (
                                                <HStack width="100%" spacing={4}>
                                                    <NumberInput
                                                        min={1}
                                                        max={10}
                                                        value={publicQuantity}
                                                        onChange={handlePublicQuantityChange}
                                                        width="100px"
                                                        sx={{
                                                            '& input': {
                                                                bg: 'white',
                                                                color: 'black',
                                                            },
                                                            '& button': {
                                                                bg: 'white',
                                                                color: 'black',
                                                                borderColor: 'gray.200',
                                                                _hover: {
                                                                    bg: 'gray.100'
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <NumberInputField />
                                                        <NumberInputStepper>
                                                            <NumberIncrementStepper />
                                                            <NumberDecrementStepper />
                                                        </NumberInputStepper>
                                                    </NumberInput>
                                                    <Button
                                                        onClick={mintPublic}
                                                        isLoading={isMinting}
                                                        width="100%"
                                                        bg="white"
                                                        color="black"
                                                        _hover={{ bg: 'gray.100' }}
                                                        colorScheme="gray"
                                                    >
                                                        <HStack justify="space-between" width="100%">
                                                            <Text>Mint {publicQuantity} Surge</Text>
                                                            <HStack>
                                                                <Text>{(PUBLIC_PRICE * Math.min(10, publicQuantity)) / 1_000_000_000}</Text>
                                                                <img src="/sui.svg" alt="SUI" width="15px" height="15px" />
                                                            </HStack>
                                                        </HStack>
                                                    </Button>
                                                </HStack>
                                            )}
                                        </>
                                    )}
                                </VStack>
                            )}

                            {/* Stats Card - Always visible */}
                            <StatsCard 
                                stats={collectionState} 
                                constants={{
                                    MAX_SUPPLY,
                                    PHASE1_ALLOC,
                                    PHASE2_ALLOC
                                }}
                                mintingPhase={mintingPhase}
                                timeRemaining={formatTime(timeRemaining)}
                            />
                        </VStack>
                    </Box>
                </VStack>
            </Box>
        </div>
    );
};

export default FullScreenOverlay; 